import { useState } from "react";
import { Plus, FileText, Users, Calendar, Workflow, Search, Command } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: any;
  action: () => void;
  keywords: string[];
}

export function QuickActions() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const actions: QuickAction[] = [
    {
      id: "new-job",
      title: "Create New Job",
      description: "Start a new roofing project",
      icon: Plus,
      action: () => {
        navigate("/jobs/new");
        setOpen(false);
      },
      keywords: ["create", "new", "job", "project"],
    },
    {
      id: "new-lead",
      title: "Create New Lead",
      description: "Add a potential customer",
      icon: Users,
      action: () => {
        navigate("/leads/new");
        setOpen(false);
      },
      keywords: ["create", "new", "lead", "customer"],
    },
    {
      id: "forms",
      title: "View Forms",
      description: "Access form templates and submissions",
      icon: FileText,
      action: () => {
        navigate("/templates");
        setOpen(false);
      },
      keywords: ["forms", "templates", "submissions"],
    },
    {
      id: "dispatch",
      title: "Dispatch Board",
      description: "Schedule and manage crews",
      icon: Calendar,
      action: () => {
        navigate("/dispatch");
        setOpen(false);
      },
      keywords: ["dispatch", "schedule", "crews", "calendar"],
    },
    {
      id: "workflows",
      title: "Workflow Automation",
      description: "Manage automated workflows",
      icon: Workflow,
      action: () => {
        navigate("/workflow-automation");
        setOpen(false);
      },
      keywords: ["workflow", "automation", "process"],
    },
    {
      id: "analytics",
      title: "Analytics Dashboard",
      description: "View performance metrics",
      icon: Search,
      action: () => {
        navigate("/analytics");
        setOpen(false);
      },
      keywords: ["analytics", "metrics", "reports", "insights"],
    },
  ];

  const filteredActions = actions.filter(
    (action) =>
      action.title.toLowerCase().includes(search.toLowerCase()) ||
      action.description.toLowerCase().includes(search.toLowerCase()) ||
      action.keywords.some((keyword) =>
        keyword.toLowerCase().includes(search.toLowerCase())
      )
  );

  // Keyboard shortcut to open
  useState(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <>
      <Button
        variant="outline"
        className="gap-2"
        onClick={() => setOpen(true)}
      >
        <Command className="h-4 w-4" />
        Quick Actions
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Quick Actions</DialogTitle>
            <DialogDescription>
              Search for actions or navigate quickly
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Search actions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredActions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No actions found
                </p>
              ) : (
                filteredActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={action.action}
                    className={cn(
                      "w-full flex items-start gap-3 p-3 rounded-lg",
                      "hover:bg-muted transition-colors text-left"
                    )}
                  >
                    <div className="p-2 rounded-lg bg-primary/10">
                      <action.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{action.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {action.description}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
